import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import {
    catchError,
    combineLatest,
    filter,
    from,
    map,
    mergeMap,
    Observable,
    of,
    retry,
    Subscription,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { Router } from '@angular/router';
import { ConfigConstants } from '../../config/config-constants';
import { PwaPushNotificationsLocalStorageService } from './pwa-push-notifications.local-storage.service';
import { PwaPushNotificationsUserSettings } from './pwa-push-notifications-user-settings.type';
import { PwaPushNotificationsRequest } from './pwa-push-notifications-request.type';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SubscribePushNotificationVisualGuideComponent } from '../../components/subscribe-push-notifications-visual-guide/subscribe-push-notifications-visual-guide.component';
import { isDefined } from '../../utilities/is-defined';
import {
    AdditionalDataPushNotificationUpdate,
    isPushNotificationUpdateForNotification,
    PushNotificationUpdateForNotification,
} from './pwa-push-notification-stored-data.type';
import { UserMasterService } from '../user-master/user-master.service';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { OfflineService } from '@evorait/pace-offline';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { AppNotificationService } from '../app-notification/app-notification.service';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '../config/config.service';
import { EnvironmentService } from '@evorait/pace-environment';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

@Injectable({
    providedIn: 'root',
})
export class PwaPushNotificationService implements InitializableInterface {
    public isInitialized = false;

    public notificationClicks$: Observable<boolean>;
    private notificationClickSubscription: Subscription;
    public messages$: Observable<object>;
    private messagesSubscription: Subscription;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly environmentService: EnvironmentService,
        private readonly swPush: SwPush,
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly pwaPushNotificationsLocalStorageService: PwaPushNotificationsLocalStorageService,
        private readonly modalService: BsModalService,
        private readonly userMasterService: UserMasterService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        private readonly offlineService: OfflineService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translateService: TranslateService,
        private readonly configService: ConfigService,
    ) {
        this.notificationClicks$ = this.swPush.notificationClicks.pipe(
            filter(isPushNotificationUpdateForNotification),
            switchMap((pushUpdateForNotification) =>
                of(pushUpdateForNotification).pipe(
                    mergeMap(() =>
                        this.offlineService.isSyncing$.pipe(
                            // block emission of events until offline service is done syncing
                            filter((isSyncInProgress) => !isSyncInProgress),
                        ),
                    ),
                    mergeMap(() =>
                        this.router.navigate([
                            ConfigConstants.routeUrlDetails,
                            pushUpdateForNotification.notification.data.gid,
                            pushUpdateForNotification.notification.data.notificationNumber,
                            pushUpdateForNotification.notification.data.taskNumber,
                        ]),
                    ),
                ),
            ),
        );

        this.messages$ = this.swPush.messages.pipe(
            mergeMap((message) =>
                combineLatest([
                    this.synchronizationService.receiveNotifications(),
                    this.synchronizationService.receiveWhatsNew().pipe(
                        tap(() => {
                            if (
                                this.isAdditionalDataPresent(
                                    message as
                                        | PushNotificationUpdateForNotification
                                        | AdditionalDataPushNotificationUpdate,
                                )
                            ) {
                                const casted = message as AdditionalDataPushNotificationUpdate;
                                const additionalData = casted.additionalData?.data;

                                if (isDefined(additionalData)) {
                                    this.appNotificationService.setAppNotification(
                                        AppNotificationType.INFO,
                                        <string>this.translateService.instant('PUSH_NOTIFICATION_TITLE'),
                                        `${additionalData}`,
                                    );
                                }
                            }
                        }),
                        map(() => message),
                        catchError((error) => {
                            this.log.error(
                                `PwaPushNotificationService: setupPushNotificationReceivedHandler: error processing message: ${JSON.stringify(
                                    error,
                                )}`,
                            );
                            return throwError(() => error);
                        }),
                    ),
                ]),
            ),
        );
    }

    // First init process triggered from initialization.service
    public init(): Observable<void> {
        // Initialize PWA Push Notifications
        this.initializePwaPushNotifications();

        this.isInitialized = true;
        return of(undefined);
    }

    // Handles immediate initialization logic
    public initializePwaPushNotifications(): void {
        this.pwaPushNotificationsLocalStorageService
            .getPwaPushNotificationsUserSettingsAsync()
            .then((currentUserSettings) => {
                if (currentUserSettings?.isUserSubscribed) {
                    this.setupPushNotificationReceivedHandler();
                    this.setupNotificationClickEventHandler();
                }
            })
            .catch((error) => {
                this.log.error(this.constructor.name, 'failed to get PwaPushNotificationsUserSettingsAsync', error);
            });
    }

    // Handles the delayed subscription offer logic
    public pushNotificationSubscribe(): Observable<boolean> {
        const serverPublicKey = this.environmentService.subscriptionServerPublicKey;
        const maxRetries = 3;
        const retryDelay = 1000;

        if (!serverPublicKey) {
            return of(false);
        }

        return this.checkForNotificationPermissions().pipe(
            tap((permissionState) =>
                this.log.debug(
                    `PwaPushNotificationService:pushNotificationSubscribe: Permissions state is '${permissionState}'`,
                ),
            ),
            filter((permissionState) => permissionState === ConfigConstants.PushNotificationsPermissionState.Granted),
            mergeMap(() => from(this.swPush.requestSubscription({ serverPublicKey }))),
            mergeMap((subscription) => this.sendSubscriptionToServer(subscription)),
            mergeMap(async (response) => {
                this.log.debug(
                    `PwaPushNotificationService: pushNotificationSubscribe: Reply from push server:\n${JSON.stringify(
                        response,
                    )}`,
                );

                await this.saveUserChoicesToLocalStorage(true);
                this.setupNotificationClickEventHandler();
                this.setupPushNotificationReceivedHandler();

                return true;
            }),
            retry({
                count: maxRetries,
                delay: retryDelay,
            }),
            catchError((error) => {
                this.log.error(
                    `PwaPushNotificationService: pushNotificationSubscribe: Error after ${maxRetries} retries. Error: ${JSON.stringify(
                        error,
                    )}`,
                );
                return of(false);
            }),
        );
    }

    public sendSubscriptionToServer(subscription: PushSubscription): Observable<unknown> {
        const hostUrl = this.environmentService.subscribeNotificationsUrl;
        const uriSubscribe = '/api/subscribe';
        const userModel = this.userMasterService.getSelectedUserModel();

        const body: PwaPushNotificationsRequest = {
            gid: userModel.getFrontId(),
            salesOrg: userModel.getSalesOrg(),
            plant: userModel.getPlant(),
            country: userModel.getCountry(),
            subscription: subscription,
            env: this.configService.getBackendName(),
        };
        return this.http.post(`${hostUrl}${uriSubscribe}`, JSON.stringify(body), { responseType: 'text' });
    }

    public checkForNotificationPermissions(): Observable<ConfigConstants.PushNotificationsPermissionState> {
        if ('Notification' in window) {
            return from(Notification.requestPermission()).pipe(
                map((permission) => {
                    switch (permission) {
                        case 'granted':
                            this.log.warn('PwaPushNotificationService: Notifications: User allowed notifications');
                            return ConfigConstants.PushNotificationsPermissionState.Granted;
                        case 'denied':
                            this.log.warn('PwaPushNotificationService: Notifications: User blocked notifications');
                            return ConfigConstants.PushNotificationsPermissionState.Denied;
                        default:
                            this.log.warn('PwaPushNotificationService: Notifications: User dismissed notifications');
                            return ConfigConstants.PushNotificationsPermissionState.Default;
                    }
                }),
            );
        } else {
            this.log.warn('This browser does not support notifications');
            return of(ConfigConstants.PushNotificationsPermissionState.Denied);
        }
    }

    public askUserForPushNotificationPermission(): void {
        this.pwaPushNotificationsLocalStorageService
            .getPwaPushNotificationsUserSettingsAsync()
            .then((currentUserSettings) => {
                // ask the user to allow push notifications if either no saved settings exist (= the user was not asked so far)
                // or if the user is not subscribed and was not yet asked to subscribe
                // and if the application is installed as PWA; otherwise notifications might not be allowed at all!
                if (
                    (!isDefined(currentUserSettings) ||
                        (!currentUserSettings.isUserSubscribed &&
                            !currentUserSettings.hasUserBeenOfferedToSubscribe)) &&
                    window.matchMedia('(display-mode: standalone)').matches
                ) {
                    this.modalService.show(SubscribePushNotificationVisualGuideComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_LARGE,
                        ariaLabelledBy: 'subscribe-push-notification-modal-title',
                    });
                }
            })
            .catch((error) => {
                this.log.error(this.constructor.name, 'failed to get PwaPushNotificationsUserSettingsAsync', error);
            });
    }

    private setupPushNotificationReceivedHandler(): void {
        if (isDefined(this.messagesSubscription)) {
            this.messagesSubscription.unsubscribe();
        }
        this.messagesSubscription = this.messages$.subscribe();
    }

    private setupNotificationClickEventHandler(): void {
        if (isDefined(this.notificationClickSubscription)) {
            this.notificationClickSubscription.unsubscribe();
        }
        this.notificationClickSubscription = this.notificationClicks$.subscribe();
    }

    public async saveUserChoicesToLocalStorage(isUserSubscribed: boolean): Promise<void> {
        const userPushNotificationsSettings: PwaPushNotificationsUserSettings = {
            isUserSubscribed: isUserSubscribed,
            hasUserBeenOfferedToSubscribe: true,
        };

        return this.pwaPushNotificationsLocalStorageService.savePwaPushNotificationsUserSettingsAsync(
            userPushNotificationsSettings,
        );
    }

    private isAdditionalDataPresent(
        message: PushNotificationUpdateForNotification | AdditionalDataPushNotificationUpdate,
    ): message is AdditionalDataPushNotificationUpdate {
        return 'additionalData' in message && typeof message.additionalData?.data === 'string';
    }
}
